import { errorForm, navigation, task } from '@origin-digital/reporting-client';
import { AxiosPromise } from 'axios';
import * as React from 'react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { AccountResponse } from 'ts/services/find-account/AccountResponse';
import { FindEmailView } from './FindEmailView';
import { Content } from './Interfaces';
import { LiveChat } from '../../../../components/LiveChat';

interface FindEmailPageProps {
  history: any;
  findAccountAction: (
    lastname: string,
    dob: string,
    postcode: string,
  ) => AxiosPromise<AccountResponse>;
  nextAction: (email: string) => void;
  conflictAccountAction: (lastName: string, dob: string, postcode: string) => void;
  errorPageAction: (statusCode: number) => void;
  content: Content;
}

interface FindEmailPageState {
  anyTextFieldChanged: boolean;
  lastName: string;
  lastNameError: string;
  dob: string;
  dobError: string;
  postcode: string;
  postcodeError: string;
  spinner: boolean;
  alertBoxError: string;
}

const FindEmailPage: React.FC<FindEmailPageProps> = ({
  history,
  findAccountAction,
  nextAction,
  conflictAccountAction,
  errorPageAction,
  content,
}) => {
  const [state, setState] = useState<FindEmailPageState>({
    anyTextFieldChanged: false,
    lastName: '',
    lastNameError: '',
    dob: '',
    dobError: '',
    postcode: '',
    postcodeError: '',
    spinner: false,
    alertBoxError: '',
  });

  const DATE_REGEX = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/;

  const POSTCODE_REGEX = /^\d{4}$/;

  useEffect(() => {
    navigation('screen', {
      currentUri: '',
      friendlyUri: content.analytics.navigation.friendlyUri,
    });
  }, []);

  const onSubmit = async (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    let containsValidationError = false;
    // validate all fields & show inline error if any are invalid
    if (!validatePostcode(state.postcode)) {
      containsValidationError = true;
    }
    if (!validateDOB(state.dob)) {
      containsValidationError = true;
    }
    if (!validateLastName(state.lastName)) {
      containsValidationError = true;
    }
    if (containsValidationError) {
      return;
    }

    setState({ ...state, spinner: true, alertBoxError: '' });

    try {
      const accountsResponse = await findAccountAction(state.lastName, state.dob, state.postcode);
      nextAction(accountsResponse.data.email);
    } catch (error) {
      setState({ ...state, spinner: false });
      const { response = {} } = error || {};
      handleSearchErrors(response.status);
    }
  };

  const handleSearchErrors = (statusCode: number) => {
    switch (statusCode) {
      case 403:
      case 404:
        handleNoAccounts();
        break;
      case 409:
        handleConflictAccount();
        break;
      default:
        errorPageAction(statusCode);
    }
  };

  const handleConflictAccount = () => {
    conflictAccountAction(state.lastName, state.dob, state.postcode);
  };

  const handleNoAccounts = () => {
    const { formName, errorMessage, errorCode } = content.analytics.errorForm;

    errorForm({
      formName,
      errorMessage,
      errorCode,
    });

    setState({
      ...state,
      spinner: false,
      alertBoxError: content.copy.alertBoxText,
    });
  };

  const onLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    textFieldChanged();
    setState({ ...state, lastName: e.target.value, lastNameError: '', anyTextFieldChanged: true });
  };

  const onDOBChange = (e: ChangeEvent<HTMLInputElement>) => {
    textFieldChanged();
    setState({ ...state, dob: e.target.value, dobError: '', anyTextFieldChanged: true });
  };

  const onPostcodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    textFieldChanged();
    setState({ ...state, postcode: e.target.value, postcodeError: '', anyTextFieldChanged: true });
  };

  const onLastNameBlur = (e: ChangeEvent<HTMLInputElement>) => {
    validateLastName(e.target.value);
  };

  const onDOBBlur = (e: ChangeEvent<HTMLInputElement>): void => {
    validateDOB(e.target.value);
  };

  const onPostcodeBlur = (e: ChangeEvent<HTMLInputElement>) => {
    validatePostcode(e.target.value);
  };

  const validateDOB = (dob: string): boolean => {
    if (!DATE_REGEX.test(dob)) {
      setState({ ...state, dobError: 'Hmm, is that DOB correct? It should be DD/MM/YYYY' });
      return false;
    }
    return true;
  };

  const validatePostcode = (postcode: string): boolean => {
    if (!POSTCODE_REGEX.test(postcode)) {
      setState({ ...state, postcodeError: 'Hmm, that postcode should just be four numbers' });
      return false;
    }
    return true;
  };

  const validateLastName = (lastName: string): boolean => {
    if (!lastName) {
      setState({ ...state, lastNameError: 'Oops, please enter your last name' });
      return false;
    }
    return true;
  };

  const textFieldChanged = () => {
    if (!state.anyTextFieldChanged) {
      task.base(
        content.analytics.taskType as any,
        {
          action: 'start',
        } as any,
      );
    }
  };

  return (
    <>
      <FindEmailView
        onAlertCloseClick={() => setState({ ...state, alertBoxError: '' })}
        history={history}
        copy={content.copy}
        onDOBChange={onDOBChange}
        onDOBBlur={onDOBBlur}
        dobError={state.dobError}
        lastNameError={state.lastNameError}
        onLastNameBlur={onLastNameBlur}
        onLastNameChange={onLastNameChange}
        onPostcodeChange={onPostcodeChange}
        onPostcodeBlur={onPostcodeBlur}
        postcodeError={state.postcodeError}
        onSubmit={onSubmit}
        spinner={state.spinner}
        alertBoxError={state.alertBoxError}
      />
      <LiveChat />
    </>
  );
};

export { FindEmailPage };
